import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as API from '../api/publications';

export interface Publication {
  id: string;
  title: string;
  content: string;
  excerpt?: string;
  author: string;
  category: string;
  imageUrl?: string;
  createdAt: Date;
  updatedAt: Date;
  published: boolean;
  tags?: string[];
}

interface PublicationsContextType {
  publications: Publication[];
  addPublication: (publication: Omit<Publication, 'id' | 'createdAt' | 'updatedAt' | 'author'>) => void;
  // We remove 'author' from input of addPublication if we rely on backend for it, but existing AddPublication.tsx might pass it. 
  // API CreatePublicationData does NOT have author. So it is fine to omit.
  // But wait, AddPublication.tsx passes 'author'.
  // Let's keep it loose for now, but fixing the interface is key.
  updatePublication: (id: string, publication: Partial<Publication>) => void;
  deletePublication: (id: string) => void;
  getPublicationById: (id: string) => Publication | undefined;
  getPublishedPublications: () => Publication[];
  getPublicationsByCategory: (category: string) => Publication[];
  isLoading: boolean;
}

const PublicationsContext = createContext<PublicationsContextType | undefined>(undefined);

export const usePublications = () => {
  const context = useContext(PublicationsContext);
  if (context === undefined) {
    throw new Error('usePublications must be used within a PublicationsProvider');
  }
  return context;
};

interface PublicationsProviderProps {
  children: ReactNode;
}

export const PublicationsProvider: React.FC<PublicationsProviderProps> = ({ children }) => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPublications = async () => {
      try {
        setIsLoading(true);
        const data = await API.getPublications();
        // Ensure dates are parsed and author is stringified if needed
        const parsed = data.map((pub: any) => ({
          ...pub,
          createdAt: new Date(pub.createdAt),
          updatedAt: new Date(pub.updatedAt),
          author: pub.author && typeof pub.author === 'object' ? `${pub.author.firstName} ${pub.author.lastName}` : (pub.author || 'Inconnu')
        }));
        setPublications(parsed);
      } catch (error) {
        console.error('Error loading publications:', error);
        // Optionally handle error state
      } finally {
        setIsLoading(false);
      }
    };

    loadPublications();
  }, []);

  const addPublication = async (publicationData: Omit<Publication, 'id' | 'createdAt' | 'updatedAt' | 'author'>) => {
    try {
      // API call with data (author is inferred from token on backend)
      const newPub: any = await API.createPublication(publicationData);

      const parsedPub: Publication = {
        ...newPub,
        createdAt: new Date(newPub.createdAt),
        updatedAt: new Date(newPub.updatedAt),
        author: newPub.author && typeof newPub.author === 'object' ? `${newPub.author.firstName} ${newPub.author.lastName}` : (newPub.author || 'Moi')
      };
      setPublications(prev => [parsedPub, ...prev]);
    } catch (error) {
      console.error("Failed to create publication", error);
    }
  };

  const updatePublication = async (id: string, updates: Partial<Publication>) => {
    try {
      const updatedPub: any = await API.updatePublication(id, updates);
      const parsedPub: Publication = {
        ...updatedPub,
        createdAt: new Date(updatedPub.createdAt),
        updatedAt: new Date(updatedPub.updatedAt),
        author: updatedPub.author && typeof updatedPub.author === 'object' ? `${updatedPub.author.firstName} ${updatedPub.author.lastName}` : (updatedPub.author || 'Inconnu')
      };
      setPublications(prev =>
        prev.map(pub => (pub.id === id ? parsedPub : pub))
      );
    } catch (error) {
      console.error("Failed to update publication", error);
    }
  };

  const deletePublication = async (id: string) => {
    try {
      await API.deletePublication(id);
      setPublications(prev => prev.filter(pub => pub.id !== id));
    } catch (error) {
      console.error("Failed to delete publication", error);
    }
  };

  const getPublicationById = (id: string) => {
    return publications.find(pub => pub.id === id);
  };

  const getPublishedPublications = () => {
    return publications.filter(pub => pub.published).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  };

  const getPublicationsByCategory = (category: string) => {
    return publications.filter(pub => pub.category === category && pub.published).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  };

  const value: PublicationsContextType = {
    publications,
    addPublication,
    updatePublication,
    deletePublication,
    getPublicationById,
    getPublishedPublications,
    getPublicationsByCategory,
    isLoading
  };

  return (
    <PublicationsContext.Provider value={value}>
      {children}
    </PublicationsContext.Provider>
  );
};
